import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, ListSubheader, Paper, TextField } from '@material-ui/core';
import { IStoreState, ThunkDispatch } from '../../modules';
import { Payee, add as addPayee, remove as removePayee } from '../../modules/payees';
import React, { ChangeEvent, Component, Fragment } from 'react';
import { Theme, WithStyles, WithTheme, createStyles, withStyles } from '@material-ui/core/styles';
import { faPlus, faTrashAlt, faUserFriends } from '@fortawesome/free-solid-svg-icons';

import { ChallengeResult } from '../../modules/challenge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TransactionMedium } from '../../types/transaction';
import Typography from '@material-ui/core/Typography';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { generateAvatar } from '../../helpers/user';

const styles = (theme: Theme) => createStyles({
  root: {
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    maxWidth: '45rem',
    padding: theme.spacing(1)
  },
  headerIcon: {
    marginRight: theme.spacing(2),
  },
  progress: {
    margin: '5rem auto',
  },
  paperHeader: {
    margin: theme.spacing(2),
    color: '#666'
  },
  addPayeeItem: {
    display: 'flex',
    alignItems: 'stretch',
    padding: 0,
    '& > *': {
      flex: '1 1 auto'
    }
  },
  subheader: {
    backgroundColor: 'white'
  },
  circularProgress: {
    display: 'block',
    margin: `${theme.spacing(4)}px auto`
  }
}
)

type State = {
  error: string
  addPayeeDialog: {
    open: boolean
    loading: boolean
    type: string
    error: string | null
  }
  addPayee: Payee,
  deletePayee: Payee | null
}

class PayeesPane extends Component<Props, State> {
  state: State = {
    error: "",
    addPayeeDialog: {
      open: false,
      loading: false,
      type: "",
      error: null,
    },
    addPayee: {
      accountNumber: "",
      accountNickName: "",
      medium: TransactionMedium.Wire,
      routingCode: "",
    } as Payee,
    deletePayee: null
  }

  componentDidMount = () => {
    document.title = "Manage Payees"
  }

  handleAddPayeeDialog = (medium: TransactionMedium, subheader: string) => () => {
    this.setState({
      addPayeeDialog: {
        open: true,
        loading: false,
        type: subheader,
        error: null,
      },
      addPayee: {
        accountNickName: "",
        accountNumber: "",
        medium,
        routingCode: "",
      } as Payee
    })
  }

  handleAddPayeeChange = (attr: string) => (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      addPayee: {
        ...this.state.addPayee,
        [attr]: e.target.value
      }
    })
  }

  closeAddPayeeDialog = () => {
    this.setState({
      addPayeeDialog: {
        ...this.state.addPayeeDialog,
        open: false
      }
    })
  }

  handleAddPayee = () => {
    const { addPayee } = this.props

    let payee = this.state.addPayee as unknown as Payee

    this.setState({
      addPayeeDialog: {
        ...this.state.addPayeeDialog,
        loading: true,
      }
    })
    let { analyzePromise, challengePromise } = addPayee(payee)
    analyzePromise.then((result: any) => {
      if (result.actionCode === "DENY")
        this.setState({
          addPayeeDialog: {
            ...this.state.addPayeeDialog,
            error: "Action denied",
            loading: false
          }
        })
      else
        challengePromise.then((result: any) => {
          if (result === ChallengeResult.None || result === ChallengeResult.Success)
            this.closeAddPayeeDialog()
          else
            this.setState({
              addPayeeDialog: {
                ...this.state.addPayeeDialog,
                error: "Challenge failed, please try again",
                loading: false,
              }
            })
        })
    })
  }

  handleRemovePayee = (payee: Payee | null) => () => {
    if (payee === null)
      return

    const { removePayee } = this.props

    removePayee(payee)
    this.handleRemovePayeeDialog(null)()
  }

  handleRemovePayeeDialog = (deletePayee: Payee | null) => () => {
    this.setState({
      deletePayee,
    })
  }

  renderPayees(subheader: string, medium: TransactionMedium) {
    const { payees, classes } = this.props

    return (
      <Fragment>
        <ListSubheader className={classes.subheader}>{subheader}</ListSubheader>
        {
          payees.list
            .filter((payee: Payee) => payee.medium === medium)
            .map((payee: Payee, i: number) => (
              <ListItem key={i}>
                <ListItemIcon>
                  {generateAvatar(payee.accountNickName)}
                </ListItemIcon>
                <ListItemText primary={payee.accountNickName} secondary={payee.accountNumber}>
                </ListItemText>
                <ListItemSecondaryAction>
                  <IconButton onClick={this.handleRemovePayeeDialog(payee)}>
                    <FontAwesomeIcon icon={faTrashAlt} size="xs" fixedWidth />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
        }
        <ListItem className={classes.addPayeeItem}>
          <Button color="primary" onClick={this.handleAddPayeeDialog(medium, subheader)}><FontAwesomeIcon icon={faPlus} fixedWidth /> Add new payee</Button>
        </ListItem>
      </Fragment>
    )
  }

  render = () => {
    const { classes } = this.props
    const { addPayeeDialog, addPayee, deletePayee } = this.state

    return (
      <Fragment>
        <Paper className={classes.root}>
          <Typography variant="h4" className={classes.paperHeader}><FontAwesomeIcon icon={faUserFriends} className={classes.headerIcon} /> Manage Payees</Typography>
          <List>
            {this.renderPayees("Local Accounts", TransactionMedium.Wire)}
            {this.renderPayees("Billing Organizations", TransactionMedium.BillpayElectronic)}
          </List>
        </Paper>
        <Dialog open={addPayeeDialog.open} maxWidth="sm" fullWidth>
          <DialogTitle>{addPayeeDialog.type} - Add Payee</DialogTitle>

          {addPayeeDialog.loading
            ? <CircularProgress className={classes.circularProgress} />
            : <Fragment>
              <DialogContent>
                {addPayeeDialog.error &&
                  <DialogContentText>
                    <Typography variant="subtitle2" color="error">{addPayeeDialog.error}</Typography>
                  </DialogContentText>
                }
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      autoFocus
                      margin="normal"
                      value={addPayee.accountNickName}
                      onChange={this.handleAddPayeeChange('accountNickName')}
                      label="Payee Name"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      margin="normal"
                      value={addPayee.accountNumber}
                      onChange={this.handleAddPayeeChange('accountNumber')}
                      label="Account Number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      margin="normal"
                      value={addPayee.routingCode}
                      onChange={this.handleAddPayeeChange('routingCode')}
                      label="Routing Code"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={this.closeAddPayeeDialog}>Cancel</Button>
                <Button onClick={this.handleAddPayee} color="primary">Add</Button>
              </DialogActions>
            </Fragment>
          }
        </Dialog>
        {deletePayee &&
          <Dialog open={Boolean(deletePayee)}>
            <DialogTitle>Delete Payee</DialogTitle>
            <DialogContent>
              <DialogContentText>Are you sure you want to delete <strong>{deletePayee!.accountNickName}</strong> from your payee list?</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleRemovePayeeDialog(null)}>
                Cancel
          </Button>
              <Button onClick={this.handleRemovePayee(deletePayee)} color="secondary">
                Delete
          </Button>
            </DialogActions>
          </Dialog>
        }
      </Fragment>
    )
  }
}

const mapStateToProps = ({ payees, intl }: IStoreState) => ({
  payees,
  intl,
})

const mapDispatchToProps = (dispatch: ThunkDispatch) =>
  bindActionCreators(
    {
      addPayee,
      removePayee
    }, dispatch
  )

interface Props extends WithTheme, WithStyles<typeof styles>, ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps> {
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(PayeesPane));